import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    notificationService,
    NotificationItem,
    NotificationPage,
    InboxQueryParams,
} from "@/entities/notification/api/notificationService";
import { notificationWebSocket, NotificationWsMessage } from "@/entities/notification/api/notificationWebSocket";
import { chatWebSocket } from "@/entities/chat/api/chatWebSocket";
import { userService } from "@/entities/user/api/userService";
import { getToken, getUserIdFromToken } from "@/shared/lib/token";
import { useToast } from "@/shared/ui/toast/ToastContext";
import { useRouter } from "@/shared/lib/router/router";
import { useAuth } from "@/shared/model/authContext";
import { openNotificationLink } from "@/features/notification-center/lib/openLink";

const FETCH_COOLDOWN_MS = 5_000;

// ─── Types ────────────────────────────────────────────────────────────────────

interface NotificationContextType {
    notifications: NotificationItem[];
    totalUnread: number;
    totalPages: number;
    currentPage: number;
    isLoading: boolean;
    fetchInbox: (params?: InboxQueryParams) => Promise<void>;
    markAsViewed: (id: string) => Promise<void>;
    disableNotification: (id: string) => Promise<void>;
    loadNextPage: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>(
    {} as NotificationContextType
);

export const useNotifications = () => {
    const ctx = useContext(NotificationContext);
    if (!ctx) throw new Error("useNotifications must be inside NotificationProvider");
    return ctx;
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export function NotificationProvider({ children }: { children: React.ReactNode }) {
    // Só depois do AuthProvider validar/renovar o token: evita 401 + refresh duplicado no boot.
    const { isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalUnread, setTotalUnread] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const inboxRequestRef = useRef<Promise<void> | null>(null);
    const lastInboxFetchAtRef = useRef(0);

    const { system: systemToast, chatMessage: chatToast } = useToast();
    const router = useRouter();

    // ── Helpers ──────────────────────────────────────────────────────────────

    const syncUnreadCount = useCallback((items: NotificationItem[]) => {
        setTotalUnread(items.filter((n) => !n.read).length);
    }, []);

    // ── REST: fetch inbox ─────────────────────────────────────────────────────

    const fetchInbox = useCallback(
        async (params: InboxQueryParams = {}) => {
            const token = getToken();
            if (!token) return;

            const now = Date.now();
            const hasCustomParams = Object.keys(params).length > 0;
            if (!hasCustomParams && now - lastInboxFetchAtRef.current < FETCH_COOLDOWN_MS) {
                return;
            }

            if (inboxRequestRef.current) {
                return inboxRequestRef.current;
            }

            lastInboxFetchAtRef.current = now;

            const run = (async () => {
                setIsLoading(true);
                try {
                    const data: NotificationPage = await notificationService.getInbox({
                        page: 0,
                        size: 20,
                        ...params,
                    });

                    setNotifications(data.content);
                    setTotalPages(data.totalPages);
                    setCurrentPage(data.number);
                    syncUnreadCount(data.content);
                } catch (err) {
                    console.warn("[NotificationContext] Inbox unavailable:", err);
                } finally {
                    setIsLoading(false);
                    inboxRequestRef.current = null;
                }
            })();

            inboxRequestRef.current = run;
            return run;
        },
        [syncUnreadCount]
    );

    // ── REST: load next page ──────────────────────────────────────────────────

    const loadNextPage = useCallback(async () => {
        const nextPage = currentPage + 1;
        if (nextPage >= totalPages) return;

        const token = getToken();
        if (!token) return;

        try {
            const data = await notificationService.getInbox({
                page: nextPage,
                size: 20,
            });

            setNotifications((prev) => {
                const ids = new Set(prev.map((n) => n.id));
                const newItems = data.content.filter((n) => !ids.has(n.id));
                const merged = [...prev, ...newItems];
                syncUnreadCount(merged);
                return merged;
            });
            setCurrentPage(data.number);
            setTotalPages(data.totalPages);
        } catch (err) {
            console.error("[NotificationContext] Error loading next page:", err);
        }
    }, [currentPage, totalPages, syncUnreadCount]);

    // ── REST: mark as viewed ──────────────────────────────────────────────────

    const markAsViewed = useCallback(
        async (id: string) => {
            try {
                const updated = await notificationService.markAsViewed(id);
                setNotifications((prev) => {
                    const next = prev.map((n) => (n.id === id ? updated : n));
                    syncUnreadCount(next);
                    return next;
                });
            } catch (err) {
                console.error("[NotificationContext] Error marking as viewed:", err);
            }
        },
        [syncUnreadCount]
    );

    // ── REST: disable (hide) ──────────────────────────────────────────────────

    const disableNotification = useCallback(
        async (id: string) => {
            try {
                await notificationService.disableNotification(id);
                setNotifications((prev) => {
                    const next = prev.filter((n) => n.id !== id);
                    syncUnreadCount(next);
                    return next;
                });
            } catch (err) {
                console.error("[NotificationContext] Error disabling notification:", err);
            }
        },
        [syncUnreadCount]
    );

    // ── WebSocket: real-time notifications ───────────────────────────────────

    useEffect(() => {
        const token = getToken();
        if (!isAuthenticated || !token) return;

        const handleNewNotification = (wsMsg: NotificationWsMessage) => {
            const item: NotificationItem = {
                id: wsMsg.id,
                title: wsMsg.title,
                message: wsMsg.message,
                type: wsMsg.type,
                read: wsMsg.read ?? false,
                createdAt: wsMsg.createdAt,
                readedAt: (wsMsg.readedAt as string | null) ?? null,
                link: typeof wsMsg.link === "string" ? wsMsg.link : undefined,
                // Defensivo: o backend pode (vir a) incluir o contexto do chat.
                topicId: typeof wsMsg.topicId === "string" ? wsMsg.topicId : undefined,
                senderId: typeof wsMsg.senderId === "string" ? wsMsg.senderId : undefined,
            };

            setNotifications((prev) => {
                if (prev.some((n) => n.id === item.id)) return prev;
                const next = [item, ...prev];
                syncUnreadCount(next);
                return next;
            });

            if (item.type === "CHAT_MESSAGE" && !router.pathname.startsWith("/crm")) {
                const topicId = item.topicId;
                const senderId = item.senderId;

                const showChatToast = (senderName: string) => {
                    chatToast(item.message, senderName, {
                        onClick: () => router.push("/crm/chat"),
                        // Resposta inline só quando há contexto da conversa.
                        onReply: topicId
                            ? (text: string) => {
                                  const myId = getUserIdFromToken();
                                  if (!myId) return;
                                  chatWebSocket.ensureConnected();
                                  chatWebSocket.sendMessage(topicId, myId, text);
                              }
                            : undefined,
                        replyPlaceholder: `Responder a ${senderName}...`,
                    });
                };

                // O título do backend é o nome do tópico; resolvemos o nome de
                // quem enviou (senderId) para o toast mostrar de quem é a mensagem.
                if (senderId) {
                    userService
                        .getChatProfile(senderId)
                        .then((p) => {
                            // Nome pode vir vazio (firstName/lastName null); cai para
                            // a parte local do email e, por fim, para o título do tópico.
                            const fullName = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim();
                            const emailLocal = p.email ? p.email.split("@")[0] : "";
                            showChatToast(fullName || emailLocal || item.title);
                        })
                        .catch(() => showChatToast(item.title));
                } else {
                    showChatToast(item.title);
                }
            } else {
                systemToast(
                    item.message,
                    item.title,
                    item.link ? () => openNotificationLink(item.link, router) : undefined
                );
            }
        };

        notificationWebSocket.connect(() => {
            notificationWebSocket.subscribeToUserNotifications(handleNewNotification);
        });

        return () => {
            notificationWebSocket.unsubscribeFromUserNotifications();
            notificationWebSocket.disconnect();
        };
    }, [isAuthenticated, syncUnreadCount, systemToast, chatToast, router]);

    // ── Mount: initial REST fetch ─────────────────────────────────────────────

    useEffect(() => {
        const token = getToken();
        if (!isAuthenticated || !token) return;

        fetchInbox();

        return () => {
            inboxRequestRef.current = null;
        };
    }, [isAuthenticated, fetchInbox]);

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                totalUnread,
                totalPages,
                currentPage,
                isLoading,
                fetchInbox,
                markAsViewed,
                disableNotification,
                loadNextPage,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
}
